import React, { useRef } from 'react';
import {
  Button,
  useColorMode,
  useColorStyle,
  Box,
  Flex,
  Space,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@tonic-one/react';
import { FormLabel, FormTextarea } from '@Components/Common/FormComponent';
import { AddOrgInfo, UpdateOrgInfo } from '../../utils/types';
import { useTranslation } from 'react-i18next';
import {
  InputChildRef,
  InputWithValid,
} from '@Components/Common/Form/InputWithValid';
import { ValidProps } from '@Types/index';
import {
  validateIsNotWhitespace,
  validateOrgNameLength,
} from '@Utils/validate';
import config from '../../../../config';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

const AddAndEditOrgModal = React.memo(
  (props: {
    isVisible: boolean;
    onClose: () => void;
    onSaveNew: (addOrgInfo: AddOrgInfo) => void;
    onUpdate: (updateOrgInfo: UpdateOrgInfo) => void;
    title: string;
    updateOrgInfo?: UpdateOrgInfo;
  }) => {
    const { isVisible, onClose, onSaveNew, onUpdate, title, updateOrgInfo } =
      props;
    const { t } = useTranslation();
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    const inputChildRef = useRef<InputChildRef>(null);
    const { widthEditablePermission } = usePermissionStore();
    // const textareaChildRef = useRef<InputChildRef>(null);
    const validateOrgName: ValidProps[] = [
      {
        validMethod: validateIsNotWhitespace,
        errMsg: t('errors.administrator.org.emptyOrgNameTitle'),
      },
      {
        validMethod: validateOrgNameLength,
        errMsg: t('errors.administrator.org.orgNameMaxlength'),
      },
    ];
    const [orgInfo, setOrgInfo] = React.useState<AddOrgInfo>(
      updateOrgInfo
        ? {
            orgName: updateOrgInfo.orgName,
            orgDescription: updateOrgInfo.orgDescription,
          }
        : {
            orgName: '',
            orgDescription: '',
          }
    );
    const onSave = async (orgInfo: AddOrgInfo) => {
      inputChildRef.current?.validate(orgInfo.orgName);
      if (inputChildRef.current?.errorMsg !== undefined) return;
      if (updateOrgInfo) {
        onUpdate({
          orgId: updateOrgInfo.orgId,
          orgName: orgInfo.orgName,
          orgDescription: orgInfo.orgDescription,
        });
      } else {
        onSaveNew(orgInfo);
      }
    };
    return (
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={isVisible}
        onClose={onClose}
        size="sm"
      >
        <ModalOverlay opacity={0.9} />
        <ModalContent backgroundColor={colorStyle.background.secondary}>
          <ModalHeader>{title}</ModalHeader>
          <ModalBody ml="24px" mt="8px">
            <Box>
              <Flex mt="20px" alignItems="flex-start">
                <FormLabel label={t('common.label.enterName')} required />
                <InputWithValid
                  ref={inputChildRef}
                  onChange={(e: any) => {
                    setOrgInfo({ ...orgInfo, orgName: e });
                  }}
                  width="286px"
                  value={orgInfo.orgName}
                  validates={validateOrgName}
                  maxLength={256}
                />
              </Flex>
              <Flex mt="16px" alignItems="flex-start">
                <FormLabel label={t('common.label.description')} />
                <FormTextarea
                  height="fit-content"
                  maxLength={
                    config.ui.globalSettings.limitation.orgTextAreaMaxLength
                  }
                  value={orgInfo.orgDescription}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setOrgInfo({ ...orgInfo, orgDescription: e.target.value });
                  }}
                />
              </Flex>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              minWidth="20x"
              onClick={() => onSave(orgInfo)}
              disabled={
                !widthEditablePermission(PermissionNameEnum.administrator)
              }
            >
              {t('common.button.save')}
            </Button>
            <Space width="2x" />
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
);

export default AddAndEditOrgModal;
